import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, CheckCircle2, AlertOctagon, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listGeofenceBreaches, acknowledgeBreach } from "@/lib/review.functions";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/review/geofence")({
  component: GeofenceReviewPage,
});

function GeofenceReviewPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const fetchBreaches = useServerFn(listGeofenceBreaches);
  const qc = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["review-geofence", orgId, from, to],
    queryFn: () =>
      fetchBreaches({
        data: { orgId: orgId!, from: from || undefined, to: to || undefined, limit: 150 },
      }),
    enabled: !!orgId,
  });
  const [ackTarget, setAckTarget] = useState<number | null>(null);
  const [ackNote, setAckNote] = useState("");

  const ackFn = useServerFn(acknowledgeBreach);
  const ack = useMutation({
    mutationFn: (vars: { id: number; note: string }) =>
      ackFn({ data: { orgId: orgId!, id: vars.id, note: vars.note || undefined } }),
    onSuccess: () => {
      toast.success("Breach acknowledged");
      setAckTarget(null);
      setAckNote("");
      qc.invalidateQueries({ queryKey: ["review-geofence", orgId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading breaches…
      </div>
    );
  }
  const breaches = data?.breaches ?? [];
  const filtered = breaches.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      b.geofenceName.toLowerCase().includes(q) ||
      b.siteName.toLowerCase().includes(q) ||
      b.guardName.toLowerCase().includes(q)
    );
  });
  if (filtered.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
        No geofence breaches recorded.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <Field label="From">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="Search">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search breaches..."
              className="rounded-md border border-white/10 bg-black/40 pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-[#666]"
            />
          </div>
        </Field>
      </div>

      <ul className="divide-y divide-white/5 overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
        {filtered.map((b) => (
          <li
            key={b.id}
            className="flex flex-wrap items-start gap-3 px-4 py-3 hover:bg-white/[0.04]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#CC0000]/15 text-[#FF8A8A]">
              <AlertOctagon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white">
                {b.geofenceName} · {b.siteName}
              </div>
              <div className="mt-0.5 text-xs text-[#999999]">
                {b.guardName} · {new Date(b.occurredAt).toLocaleString()} ·{" "}
                <span className="uppercase">{b.eventType}</span>
                {b.distanceOutsideM != null && (
                  <> · <span className="text-amber-300">{b.distanceOutsideM}m outside</span></>
                )}
              </div>
              <div className="mt-1 text-[11px] text-[#666]">
                {b.latitude.toFixed(5)}, {b.longitude.toFixed(5)}
              </div>
              {b.acknowledgedAt && (
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" /> Acknowledged{" "}
                  {new Date(b.acknowledgedAt).toLocaleString()}
                  {b.acknowledgedNote && <> — “{b.acknowledgedNote}”</>}
                </div>
              )}
            </div>
            {!b.acknowledgedAt && (
              <Button
                size="sm"
                variant="outline"
                className="border-[#CC0000]/40 text-[#FF8A8A] hover:bg-[#CC0000]/10"
                onClick={() => setAckTarget(b.id)}
              >
                Acknowledge
              </Button>
            )}
          </li>
        ))}
      </ul>

      <Dialog open={ackTarget != null} onOpenChange={(o) => !o && setAckTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge breach</DialogTitle>
          </DialogHeader>
          <Textarea
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value)}
            placeholder="Optional note (e.g. spoke with guard, false positive…)"
            maxLength={500}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAckTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => ackTarget != null && ack.mutate({ id: ackTarget, note: ackNote })}
              disabled={ack.isPending}
            >
              {ack.isPending ? "Saving…" : "Acknowledge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-[#C0C0C0]">
      <span>{label}</span>
      {children}
    </label>
  );
}
